import type { Dapp } from '@src/dto';
import { buildReleaseNftMetadata } from '../../../../../api/src/shared/nft/releaseMetadataBuilder';
import { NFT_SCHEMA_VERSION } from '../../../constants/nft';
import { AuthStateType, type AuthState } from '../../../hooks/useAuth';
import { resolveContactEmail } from '../../../utils/dappHelpers';

interface BuildReleaseNftMetadataPreviewParams {
  authState: AuthState;
  dapp: Dapp;
}

export function buildReleaseNftMetadataPreview({
  authState,
  dapp,
}: BuildReleaseNftMetadataPreviewParams) {
  const contactEmail = resolveContactEmail(dapp);
  const authenticatedUser =
    authState.type === AuthStateType.Authenticated ? authState.user : null;

  return buildReleaseNftMetadata({
    schemaVersion: NFT_SCHEMA_VERSION,
    name: dapp.dappName || '',
    description: dapp.subtitle || 'Release NFT',
    image: dapp.dappIconUrl,
    externalUrl: dapp.publisherWebsite,
    includeExternalUrlWhenNullish: true,
    creators: [
      {
        address: dapp.walletAddress || '[PUBLISHER_WALLET_ADDRESS]',
        share: 100,
      },
    ],
    publisherDetails: {
      name: dapp.publisherName,
      website: dapp.publisherWebsite,
      contact: contactEmail || authenticatedUser?.email || '[PUBLISHER_EMAIL]',
      support_email:
        dapp.supportEmail ||
        contactEmail ||
        authenticatedUser?.email ||
        '[SUPPORT_EMAIL]',
    },
    releaseDetails: {
      updated_on: new Date().toISOString(),
      license_url: dapp.licenseUrl || '',
      copyright_url: '',
      privacy_policy_url: dapp.privacyPolicyUrl || '',
      localized_resources: {
        long_description: 'uid_long_desc',
        new_in_version: 'uid_new_version',
        short_description: 'uid_short_desc',
        name: 'uid_name',
      },
    },
    androidDetails: {
      android_package: dapp.packageName || '[TO_BE_EXTRACTED_FROM_APK]',
      version: '[TO_BE_EXTRACTED_FROM_APK]',
      version_code: 0,
      min_sdk: 0,
      target_sdk: 0,
      cert_fingerprint: '[TO_BE_EXTRACTED_FROM_APK]',
      permissions: ['[TO_BE_EXTRACTED_FROM_APK]'],
      locales: ['en-US'],
    },
    media: [
      {
        mime: 'image/png',
        purpose: 'icon',
        uri: dapp.dappIconUrl,
        size: 512 * 512 * 4,
        width: 512,
        height: 512,
        sha256: '[TO_BE_CALCULATED]',
      },
      ...(dapp.dappPreviewUrls || []).map((url, index) => ({
        mime: 'image/png',
        purpose: 'screenshot',
        uri: url,
        size: 1080 * 1920 * 4,
        width: 1080,
        height: 1920,
        sha256: `[TO_BE_CALCULATED_${index + 1}]`,
      })),
      ...(dapp.bannerUrl
        ? [
            {
              mime: 'image/png',
              purpose: 'banner',
              uri: dapp.bannerUrl,
              size: 1024 * 500 * 4,
              width: 1024,
              height: 500,
              sha256: '[TO_BE_CALCULATED]',
            },
          ]
        : []),
      ...(dapp.featureGraphicUrl
        ? [
            {
              mime: 'image/png',
              purpose: 'featureGraphic',
              uri: dapp.featureGraphicUrl,
              size: 1024 * 500 * 4,
              width: 1024,
              height: 500,
              sha256: '[TO_BE_CALCULATED]',
            },
          ]
        : []),
    ],
    files: [
      {
        mime: 'application/vnd.android.package-archive',
        purpose: 'install',
        uri: '[APK_WILL_BE_UPLOADED_TO_ARWEAVE]',
        size: 0,
        sha256: '[APK_HASH_TO_BE_CALCULATED]',
      },
    ],
    i18n: {
      'en-US': {
        uid_long_desc: dapp.description || '',
        uid_new_version: '[WHATS_NEW_TO_BE_PROVIDED]',
        uid_short_desc: dapp.subtitle || '',
        uid_name: dapp.dappName || '',
      },
    },
  });
}
